import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Home as HomeIcon, Store, Receipt, Calendar, ListChecks,
  Users, Building2, Search, ChevronRight, HelpCircle,
} from 'lucide-react';
import LanguageToggle from '../components/LanguageToggle';
import BottomNavigation from '../components/BottomNavigation';

export default function HelpGuide() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const options = [
    {
      key: 'shops', icon: Store, gradient: 'from-emerald-500 to-green-600',
      title: t('help.shopsTitle', 'Shops & Services'),
      body: t('help.shopsBody', 'Find local businesses and services near you — search by name, browse by category or sort by what\u2019s nearest.'),
      to: '/explore', cta: t('help.openShops', 'Browse businesses'),
    },
    {
      key: 'prices', icon: Receipt, gradient: 'from-sky-500 to-blue-600',
      title: t('help.pricesTitle', 'Compare Prices'),
      body: t('help.pricesBody', 'Share and compare everyday shopping prices to save money. Scan a receipt with Price-2-Day or check Home & Local Prices for fuel, firewood, DIY and more.'),
      to: '/price', cta: t('help.openPrices', 'Open Price-2-Day'),
    },
    {
      key: 'whatson', icon: Calendar, gradient: 'from-pink-500 to-rose-500',
      title: t('help.whatsonTitle', "What's On"),
      body: t('help.whatsonBody', 'Discover local events, activities and special offers happening around you.'),
      to: '/events', cta: t('help.openWhatson', 'See events'),
    },
    {
      key: 'organiser', icon: ListChecks, gradient: 'from-violet-500 to-purple-600',
      title: t('help.organiserTitle', 'My Organiser'),
      body: t('help.organiserBody', 'Keep a calendar, reminders, shopping lists and your favourite places all in one place.'),
      to: '/calendar', cta: t('help.openOrganiser', 'Open my calendar'),
    },
    {
      key: 'community', icon: Users, gradient: 'from-orange-500 to-amber-500',
      title: t('help.communityTitle', 'My Community'),
      body: t('help.communityBody', 'Local information, community updates and safety alerts for the towns you care about.'),
      to: '/community-hub', cta: t('help.openCommunity', 'Open community'),
    },
    {
      key: 'business', icon: Building2, gradient: 'from-teal-500 to-cyan-600',
      title: t('help.businessTitle', 'My Business'),
      body: t('help.businessBody', 'Register, promote or manage your business. See the free and paid membership options, 60-day free trials, ratings and trust badges.'),
      to: '/business-guide', cta: t('help.openBusiness', 'How businesses work'),
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="help-guide-page">
      {/* HEADER */}
      <header className="bg-gradient-to-b from-[#132449] to-[#1e2f5c] text-white px-4 pt-5 pb-7 rounded-b-3xl shadow-lg">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-white/90 hover:text-white bg-white/10 rounded-full px-3 py-1.5"
            data-testid="help-home-button"
          >
            <HomeIcon className="h-5 w-5" />
            <span className="text-sm font-semibold">{t('help.home', 'Home')}</span>
          </button>
          <LanguageToggle />
        </div>
        <div className="mt-4 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1 text-[12px] font-semibold">
            <HelpCircle className="h-4 w-4" />
            {t('help.eyebrow', 'Help & How It Works')}
          </div>
          <h1 className="mt-3 text-2xl sm:text-3xl font-black leading-tight" data-testid="help-title">
            {t('help.title', 'How Find-2-Day works')}
          </h1>
          <p className="mt-2 text-white/80 text-sm leading-relaxed">
            {t('help.intro', 'Find-2-Day gives you six simple choices on the home screen. Tap any colour to explore — here\u2019s what each one does.')}
          </p>
        </div>
      </header>

      <main className="px-4 -mt-3 max-w-3xl mx-auto space-y-3">
        {/* Search tip */}
        <div className="mt-4 flex items-start gap-3 bg-white rounded-2xl shadow-md p-4" data-testid="help-search-tip">
          <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-slate-100 text-slate-700 shrink-0">
            <Search className="h-5 w-5" />
          </span>
          <p className="text-sm text-slate-700 leading-relaxed">
            {t('help.searchTip', 'In a hurry? Use the search bar at the top of the home screen to find businesses, events and offers straight away.')}
          </p>
        </div>

        {/* Six options */}
        {options.map((o) => (
          <button
            key={o.key}
            onClick={() => navigate(o.to)}
            data-testid={`help-option-${o.key}`}
            className="w-full flex items-center gap-3 bg-white rounded-2xl shadow-md p-4 text-left active:scale-[0.99] transition-transform hover:bg-slate-50"
          >
            <span className={`inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-gradient-to-br ${o.gradient} text-white shrink-0`}>
              <o.icon className="h-6 w-6" />
            </span>
            <span className="flex flex-col min-w-0 flex-1">
              <span className="text-[16px] font-black text-slate-800 leading-tight">{o.title}</span>
              <span className="text-[13px] text-slate-600 leading-snug mt-0.5">{o.body}</span>
              <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-purple-700 mt-1.5">
                {o.cta} <ChevronRight className="h-4 w-4" />
              </span>
            </span>
          </button>
        ))}

        {/* Back to home */}
        <button
          onClick={() => navigate('/')}
          data-testid="help-back-home-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-[#132449] text-white font-extrabold px-5 py-4 shadow-md active:scale-[0.99] transition-transform mt-1"
        >
          <ArrowLeft className="h-5 w-5" />
          {t('help.backHome', 'Back to Home')}
        </button>
      </main>

      <BottomNavigation />
    </div>
  );
}
